import { jsx as _jsx } from "react/jsx-runtime";
import { FlexBox } from '@dixa/lib-shared-habla';
import { produce } from 'immer';
import { memo, useCallback, useMemo, useReducer, useRef, useState, } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { v4 as uuid } from 'uuid';
import { optionOrderIndexStep } from '../../optionOrderIndexStep';
import { optionsKey } from '../../optionsKey';
import { Option } from '../option/option';
/** Represents the field for managing options of a custom select attribute. */
export const Options = memo(function Options(props) {
    const { parentName = '', parentId = null } = props;
    // TODO: [TEAMS-13693] add `AttributeFormFieldValuesHavingOptions` generic back after updating to RHF v8
    const { control, setFocus, getValues } = useFormContext();
    const [dragDragEvent, setDragDragEvent] = useState();
    const [changedValuesByUser, setChangedValuesByUser] = useReducer((state, updates) => (Object.assign(Object.assign({}, state), updates)), {});
    const optionsFormName = parentName
        ? `${parentName}.${optionsKey}`
        : `${optionsKey}`;
    const { fields, append, remove, update } = useFieldArray({
        control,
        name: optionsFormName,
        rules: parentName
            ? undefined
            : {
                validate: {
                    validOptions: (options) => {
                        return ((options !== null && options !== void 0 ? options : []).some(({ label, value }) => !!label && !!value) || 'At least one option should be valid');
                    },
                    activeOptions: (options) => {
                        const validOptions = (options !== null && options !== void 0 ? options : []).filter(({ label, value }) => label && value);
                        return ((validOptions.length > 0 &&
                            validOptions.some(({ is_deactivated }) => !is_deactivated)) ||
                            'At least one option should be active');
                    },
                },
            },
    });
    const maxOrderIndex = useRef(
    // TODO: [TEAMS-13693] remove casting after updating to RHF v8
    fields.reduce((max, { order_index }) => (order_index > max ? order_index : max), 0));
    /**
     * Represents the sorted options by order index. Backend does not sort by
     * order index when returning, so we have to perform our own sorting.
     */
    const orderedOptions = useMemo(() => 
    // TODO: [TEAMS-13693] remove casting after updating to RHF v8
    [...fields].sort(getOrderIndexDiff), [fields]);
    const fieldIdIndexMap = useMemo(() => new Map(fields.map(({ id }, index) => [id, index])), [fields]);
    const isParentDeactivated = !!useWatch({
        control,
        name: `${parentName}.is_deactivated`,
    });
    const addNewOption = useCallback(() => {
        maxOrderIndex.current += optionOrderIndexStep;
        append({
            id: uuid(),
            label: '',
            value: '',
            is_deactivated: isParentDeactivated,
            order_index: maxOrderIndex.current,
            parent_id: parentId,
        }, { shouldFocus: false });
    }, [append, isParentDeactivated, parentId]);
    const getNewOrderIndex = (sourceOptionIndex, targetOptionIndex) => {
        var _a, _b;
        const newIndex = targetOptionIndex + (sourceOptionIndex < targetOptionIndex ? 1 : -1);
        return ((((_b = (_a = orderedOptions[newIndex]) === null || _a === void 0 ? void 0 : _a.order_index) !== null && _b !== void 0 ? _b : 0) +
            orderedOptions[targetOptionIndex].order_index) /
            2);
    };
    /** Handles the event for when an option is moved. */
    const handleOptionMove = useCallback((event) => {
        setDragDragEvent(event);
    }, []);
    /** Handles the event for when an option is removed. */
    const handleOptionRemove = useCallback((index) => {
        remove(index);
        setChangedValuesByUser({ [index]: false });
    }, [remove]);
    /** Handles the event for when an option receives user input. */
    const handleOptionUserInput = useCallback(({ index, field }) => {
        const isLastOption = fields.length - 1 === index;
        if (isLastOption) {
            addNewOption();
        }
        if (field === 'value') {
            setChangedValuesByUser({ [index]: true });
        }
    }, [addNewOption, fields.length]);
    // Handles focusing the first option when new nested options are created.
    useEffectOnce(() => {
        if (parentName &&
            fields.length === 1 &&
            fields[0].label === '' &&
            fields[0].value === '') {
            setFocus(`${optionsFormName}.0.label`);
        }
    });
    // Handles appending a blank option to the list.
    useEffectOnce(() => {
        var _a, _b;
        if (fields.length === 1 ||
            (((_a = fields[fields.length - 1]) === null || _a === void 0 ? void 0 : _a.label) !== '' &&
                ((_b = fields[fields.length - 1]) === null || _b === void 0 ? void 0 : _b.value) !== '')) {
            addNewOption();
        }
    });
    // Handles moving the fields when drag and drop event changes. We need to
    // run this in an effect because React Hook Form FieldArray actions need to be
    // triggered per render. See https://react-hook-form.com/api/usefieldarray
    useUpdateEffect(() => {
        var _a, _b;
        if (dragDragEvent && orderedOptions) {
            const { dragIndex, hoverIndex } = dragDragEvent;
            const fieldDragIndex = (_a = fieldIdIndexMap.get(orderedOptions[dragIndex].id)) !== null && _a !== void 0 ? _a : -1;
            const newOrderIndex = getNewOrderIndex(dragIndex, hoverIndex);
            // TODO: [TEAMS-13693] remove casting after updating to RHF v8
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const options = getValues(optionsFormName);
            if (Math.abs(newOrderIndex - orderedOptions[hoverIndex].order_index) > 1) {
                update(fieldDragIndex, Object.assign(Object.assign({}, options[fieldDragIndex]), { order_index: Math.floor(newOrderIndex) }));
            }
            else {
                // TODO: [TEAMS-13693] remove casting after updating to RHF v8
                const tempOrderedOptions = produce(fields, (draft) => {
                    draft[fieldDragIndex] = Object.assign(Object.assign({}, draft[fieldDragIndex]), { order_index: newOrderIndex });
                    draft.sort(getOrderIndexDiff);
                });
                for (let i = tempOrderedOptions.length; i > 0; i--) {
                    const currentFieldIndex = (_b = fieldIdIndexMap.get(tempOrderedOptions[i - 1].id)) !== null && _b !== void 0 ? _b : -1;
                    update(currentFieldIndex, Object.assign(Object.assign({}, options[currentFieldIndex]), { order_index: i * optionOrderIndexStep }));
                }
            }
        }
    }, [dragDragEvent]);
    return (_jsx(FlexBox, Object.assign({ flexDirection: "column", width: "100%" }, { children: orderedOptions.map((option, index) => {
            var _a, _b;
            return (_jsx(Option, { parentName: parentName, fieldIndex: (_a = fieldIdIndexMap.get(option.id)) !== null && _a !== void 0 ? _a : -1, orderIndex: index, isLastOption: fields.length - 1 === fieldIdIndexMap.get(option.id), isValueChangedByUser: changedValuesByUser[(_b = fieldIdIndexMap.get(option.id)) !== null && _b !== void 0 ? _b : -1], isParentDeactivated: !!isParentDeactivated, onRemove: handleOptionRemove, onUserInput: handleOptionUserInput, onMove: handleOptionMove }, option.id));
        }) })));
});
/**
 * Returns the difference between the order indexes of given options.
 *
 * @param a First option
 * @param b Second option
 */
const getOrderIndexDiff = (a, b) => a.order_index - b.order_index;
